import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Drawer,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ListAltIcon from '@mui/icons-material/ListAlt';
import VideocamIcon from '@mui/icons-material/Videocam';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SmsIcon from '@mui/icons-material/Sms';
import KeyIcon from '@mui/icons-material/Key';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../contexts/AuthContext';
import DeviceEventsComponent from '../components/devices/DeviceEventsComponent';
import DeviceUploadsTab from '../components/devices/DeviceUploadsTab';
import DeviceFtpCredentialTab from '../components/devices/DeviceFtpCredentialTab';
import DeviceApiKeysTab from '../components/devices/DeviceApiKeysTab';
import DeviceLogsTab from '../components/devices/eventTabs/DeviceLogsTab';
import LiveStreamTab from '../components/devices/eventTabs/LiveStreamTab';
import DeviceSmsTab from '../components/devices/eventTabs/DeviceSmsTab';
import { Device as DeviceModel } from '../components/devices/types';

interface RelatedDevice {
  id: string;
  name: string;
  isParent: boolean;
  isSelf: boolean;
}

type SectionKey = 'events' | 'live-stream' | 'sms' | 'uploads' | 'logs' | 'api-keys' | 'ftp-credential';

interface Section {
  key: SectionKey;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

interface Group {
  label: string;
  sections: Section[];
}

const DeviceEventsPage: React.FC = () => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeKey, setActiveKey] = useState<SectionKey>('events');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [childDevices, setChildDevices] = useState<DeviceModel[]>([]);
  const [relatedDevices, setRelatedDevices] = useState<RelatedDevice[]>([]);
  // Whether the caller may manage this device's data API keys: the API says so
  // (an admin or the business's primary user, and the device is a site, not a camera)
  const [canManageApiKeys, setCanManageApiKeys] = useState(false);

  const hasFtpRole = useMemo(() => auth.hasRole('DMO_ADMIN') || auth.hasRole('DMO_FTP'), [auth]);
  const isAdmin = auth.hasRole('DMO_ADMIN');

  useEffect(() => {
    if (id) {
      loadChildDevices();
      loadRelatedDevices();
      loadCanManageApiKeys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadChildDevices = async () => {
    try {
      const res = await fetch(`/api/devices/${id}/children`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setChildDevices(data);
      }
    } catch (err) {
      console.error('Error loading child devices:', err);
    }
  };

  const loadRelatedDevices = async () => {
    try {
      const res = await fetch(`/api/devices/${id}/related`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const data: RelatedDevice[] = await res.json();
        setRelatedDevices(data);
      }
    } catch (err) {
      console.error('Error loading related devices:', err);
    }
  };

  const loadCanManageApiKeys = async () => {
    setCanManageApiKeys(false);
    try {
      const res = await fetch(`/api/devices/${id}/api-keys`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setCanManageApiKeys(res.ok);
    } catch {
      setCanManageApiKeys(false);
    }
  };

  if (!id) return null;

  const groups: Group[] = [
    {
      label: 'Monitoring',
      sections: [
        { key: 'events', label: 'Events', icon: <EventNoteIcon />, content: <DeviceEventsComponent deviceId={id} /> },
        { key: 'live-stream', label: 'Live Stream', icon: <VideocamIcon />, content: <LiveStreamTab deviceId={id} /> },
      ],
    },
    {
      label: 'Alerts',
      sections: [
        { key: 'sms', label: 'SMS Alerts', icon: <SmsIcon />, content: <DeviceSmsTab deviceId={id} /> },
      ],
    },
    {
      label: 'Data',
      sections: [
        ...(hasFtpRole ? [{ key: 'uploads' as SectionKey, label: 'Uploads', icon: <CloudUploadIcon />, content: <DeviceUploadsTab deviceId={id} childDevices={childDevices} /> }] : []),
        { key: 'logs', label: 'Logs', icon: <ListAltIcon />, content: <DeviceLogsTab deviceId={id} /> },
        ...(canManageApiKeys ? [{ key: 'api-keys' as SectionKey, label: 'API Keys', icon: <KeyIcon />, content: <DeviceApiKeysTab deviceId={id} /> }] : []),
      ],
    },
    ...(isAdmin ? [{
      label: 'System',
      sections: [
        { key: 'ftp-credential' as SectionKey, label: 'FTP Credential', icon: <VpnKeyIcon />, content: <DeviceFtpCredentialTab deviceId={id} /> },
      ],
    }] : []),
  ];

  const allSections = groups.flatMap((g) => g.sections);
  const safeKey = allSections.some((s) => s.key === activeKey) ? activeKey : allSections[0]?.key;
  const activeSection = allSections.find((s) => s.key === safeKey) ?? allSections[0];

  const handleNavigate = (key: SectionKey) => {
    setActiveKey(key);
    setMobileNavOpen(false);
  };

  const navList = (
    <List component="nav" dense sx={{ py: 1 }}>
      {groups.map((group) => (
        <li key={group.label}>
          <ul style={{ padding: 0 }}>
            <ListSubheader sx={{ lineHeight: '32px', bgcolor: 'transparent' }}>
              {group.label}
            </ListSubheader>
            {group.sections.map((section) => (
              <ListItemButton
                key={section.key}
                selected={safeKey === section.key}
                onClick={() => handleNavigate(section.key)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{section.icon}</ListItemIcon>
                <ListItemText primary={section.label} />
              </ListItemButton>
            ))}
          </ul>
        </li>
      ))}
    </List>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ whiteSpace: 'nowrap' }}>Device Events</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ pb: 1 }}>
          {relatedDevices.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Switch Device</InputLabel>
              <Select
                label="Switch Device"
                value={id}
                onChange={(e) => {
                  navigate(`/devices/${e.target.value}/events`);
                }}
              >
                {relatedDevices.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.isParent ? '📁 ' : '↳ '}{d.name}{d.isSelf ? ' (current)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button component={RouterLink} to="/devices">
            ← Back to Devices
          </Button>
          <Button component={RouterLink} to={`/devices/${id}/settings`} variant="outlined">
            Settings
          </Button>
        </Stack>
      </Box>

      {isMobile ? (
        <Box>
          <Button
            startIcon={<MenuIcon />}
            variant="outlined"
            onClick={() => setMobileNavOpen(true)}
            sx={{ mb: 1.5 }}
          >
            {activeSection?.label}
          </Button>
          <Drawer anchor="left" open={mobileNavOpen} onClose={() => setMobileNavOpen(false)}>
            <Box sx={{ width: 280 }}>{navList}</Box>
          </Drawer>
          <Paper sx={{ p: 1.5 }}>
            {activeSection?.content}
          </Paper>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Paper sx={{ width: 240, flexShrink: 0 }}>
            {navList}
          </Paper>
          <Paper sx={{ flexGrow: 1, minWidth: 0, p: 3 }}>
            {activeSection?.content}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default DeviceEventsPage;
